import { useState, useEffect, useRef } from "react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { generateCityHeroImageMutation } from "@/app/Cities/Cities/GraphQL/cityImageGenerationMutation";
import { cityRecordQuery } from "@/app/Cities/Cities/GraphQL/cityRecordQuery";
import { ImageGenerationModel } from "@/gql/graphql";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { BotMessageSquareIcon, ImageIcon, RefreshCwIcon } from "lucide-react";

type CityHeroImageCardProps = {
  cityId: string;
  heroImage: { url: string } | null | undefined;
};

export function CityHeroImageCard({
  cityId,
  heroImage,
}: CityHeroImageCardProps) {
  const { refetch } = useGqlQuery(cityRecordQuery, { id: cityId });
  const { mutateAsync: generateImage } = useGqlMutation(
    generateCityHeroImageMutation,
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(
    heroImage?.url || null,
  );
  const [timeRemaining, setTimeRemaining] = useState(0);

  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const startPolling = () => {
    setIsGenerating(true);
    setTimeRemaining(90);

    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await refetch();
        const newImageUrl = result.data?.city?.heroImage?.url || null;

        if (newImageUrl && newImageUrl !== imageUrl) {
          setImageUrl(newImageUrl);
          stopPolling();
          toast.success("Image generated successfully!");
        }
      } catch (error) {
        console.error("Error polling for image:", error);
      }
    }, 3000);
  };

  const stopPolling = () => {
    setIsGenerating(false);
    setTimeRemaining(0);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const handleGenerateImage = async () => {
    try {
      await generateImage({
        id: cityId,
        model: ImageGenerationModel.Gemini,
      });

      toast.success("Image generation started", {
        description: "This will take up to 90 seconds...",
      });
      startPolling();
    } catch (error) {
      toast.error("Failed to start image generation");
      console.error(error);
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  useEffect(() => {
    setImageUrl(heroImage?.url || null);
  }, [heroImage?.url]);

  if (isGenerating) {
    return (
      <RecordScreenCard
        title="Hero Image"
        subtitle="AI-generated image for homepage"
      >
        <EmptyState
          Icon={RefreshCwIcon}
          header="Generating Image"
          subheader={`Time remaining: ${timeRemaining}s`}
          iconWidth="animate-spin"
        >
          <div className="mt-4">
            <Button onClick={stopPolling} color="red">
              Cancel
            </Button>
          </div>
        </EmptyState>
      </RecordScreenCard>
    );
  }

  return (
    <RecordScreenCard
      title="Hero Image"
      subtitle="AI-generated image for homepage"
      buttons={
        <Button
          LeadingIcon={BotMessageSquareIcon}
          onClick={handleGenerateImage}
          color="blue"
        >
          {imageUrl ? "Regenerate" : "Generate"}
        </Button>
      }
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="City hero"
          className="max-w-xs rounded-lg border shadow-sm"
        />
      ) : (
        <EmptyState
          Icon={ImageIcon}
          header="No Hero Image"
          subheader="Generate an AI image for this city"
        />
      )}
    </RecordScreenCard>
  );
}
